// @ts-nocheck

import * as Sentry from '@sentry/react';
import { each, find } from 'lodash';

import spine from '../../lib/spine-webgl.custom';

const DEFAULT_HEIGHT = 500; // px
const DEFAULT_WIDTH = 500; // px

const loadAssets = (
  gl: unknown,
  assetManager: unknown,
  id: string,
  images: unknown,
  atlasName: string,
  atlas: string,
  jsonName: string,
  json: string,
): Promise<unknown> => {
  return new Promise((resolve, reject) => {
    assetManager.setRawData(jsonName, JSON.stringify(json));
    assetManager.loadJson(jsonName);

    each(images, (image, name) => {
      assetManager.setRawDataURI(name, image);
      return assetManager.loadTexture(image, (img) => {
        return img;
      });
    });

    assetManager.setRawData(atlasName, atlas);
    assetManager.loadTextureAtlas(atlasName);

    let req = null;

    const loop = () => {
      if (assetManager.isLoadingComplete()) {
        if (req) {
          cancelAnimationFrame(req);
        }

        if (assetManager.hasErrors()) {
          reject(assetManager.getErrors());
        } else {
          resolve(assetManager);
        }
      } else {
        req = requestAnimationFrame(loop);
      }
    };

    req = requestAnimationFrame(loop);
  });
};

type SpineElement = {
  id: string;
  skeleton: unknown;
  animations: Array<string>;
  skins: Array<string>;
  currentAnimation: spine.Skeleton;
  animationState: spine.AnimationState;
  show: boolean;
  chainAnimations: boolean;
  loop: boolean;
  forwards: boolean;
  onFinishedCallback?: (id: string) => void;
};

class SpineCanvas {
  gl: unknown;
  assetManager: unknown;
  canvas: unknown;
  renderer: spine.SceneRenderer;
  elements: { [key: string]: SpineElement };
  timeKeeper: spine.TimeKeeper;
  contextWasLost: boolean;
  hasWebglError: boolean;
  shouldRender: boolean;
  viewportWidth: number;
  viewportHeight: number;
  positionX: number;
  positionY: number;
  scale: number;
  maxWidthResolution: number | null;

  constructor(canvasElement: unknown, height: number = DEFAULT_HEIGHT, width: number = DEFAULT_WIDTH) {
    const config = { alpha: true };
    const webglContext = canvasElement.getContext('webgl', config);

    this.canvas = canvasElement;
    this.canvas.addEventListener('webglcontextlost', this.handleContextLost, false);
    this.contextWasLost = false;
    this.hasWebglError = false;
    this.shouldRender = false;

    this.gl = webglContext;
    this.elements = {};

    this.viewportHeight = height;
    this.viewportWidth = width;

    if (this.isAvailable()) {
      try {
        this.assetManager = new spine.AssetManager(webglContext);

        this.renderer = new spine.SceneRenderer(canvasElement, webglContext);
        this.renderer.premultipliedAlpha = true;

        this.timeKeeper = new spine.TimeKeeper();
        this.timeKeeper.maxDelta = 10;
      } catch (e) {
        Sentry.captureException(e);

        this.hasWebglError = true;
      }
    }
  }

  handleContextLost() {
    this.contextWasLost = true;
  }

  isAvailable() {
    const { gl, contextWasLost, hasWebglError } = this;

    return !!gl && !contextWasLost && !gl.isContextLost() && !hasWebglError;
  }

  updateTime() {
    const { timeKeeper } = this;

    if (timeKeeper) {
      timeKeeper.update();
    }
  }

  load(elementId: string, images: unknown, atlas: string, json: string): Promise<unknown> {
    if (!this.isAvailable()) {
      return Promise.reject('not available');
    }

    const atlasName = `${elementId}.atlas`;
    const jsonName = `${elementId}.json`;

    return loadAssets(this.gl, this.assetManager, elementId, images, atlasName, atlas, jsonName, json)
      .then((assetManager) => {
        const atlasTexture = assetManager.get(atlasName);
        const atlasLoader = new spine.AtlasAttachmentLoader(atlasTexture);
        const skeletonJson = new spine.SkeletonJson(atlasLoader);
        const skeletonData = skeletonJson.readSkeletonData(json);
        const animations = skeletonData.animations.map((animation) => animation.name);
        const skins = skeletonData.skins.map((skin) => skin.name);
        const skeleton = new spine.Skeleton(skeletonData);
        const animationStateData = new spine.AnimationStateData(skeleton.data);
        const animationState = new spine.AnimationState(animationStateData);

        const element: SpineElement = {
          id: elementId,
          skeleton: skeleton,
          animations: animations,
          skins: skins,
          currentAnimation: 0,
          animationState: animationState,
          show: false,
          chainAnimations: false,
          loop: false,
          forwards: false,
        };

        this.elements[elementId] = element;

        animationState.addListener({
          complete: () => {
            const element: SpineElement = this.elements[elementId];

            // Si on a plusieurs animation, on passe à la suivante, sinon on cache l'élément s'il n'est pas en forwards
            if (
              element.chainAnimations &&
              element.animations &&
              element.animations.length > element.currentAnimation + 1
            ) {
              const nextAnimation = animations[element.currentAnimation + 1];
              element.currentAnimation = element.currentAnimation + 1;
              element.animationState.setAnimation(0, nextAnimation, false);
            } else if (!element.loop) {
              if (!element.forwards) {
                element.currentAnimation = 0;
                element.show = false;
              }
              if (element.onFinishedCallback) {
                element.onFinishedCallback(element.id);
              }
            }
          },
        });
      })
      .catch(() => {
        return Promise.reject('animation load error');
      });
  }

  loadAnimations(animations: Array<unknown>): Promise<unknown> {
    const promises = [];

    for (let i = 0; i < animations.length; ++i) {
      const a = animations[i].assets;

      if (!this.hasAnimation(a.id)) {
        const p = this.load(a.id, a.images, a.atlas, a.json);
        promises.push(p);
      }
    }

    return Promise.all(promises);
  }

  deleteAnimations() {
    const { elements, gl } = this;

    each(elements, (element: SpineElement) => {
      element.show = false;
    });

    if (gl) {
      gl.clear(gl.COLOR_BUFFER_BIT);
    }
  }

  hasAnimation(animationId: string): boolean {
    const { elements } = this;

    return !!elements[animationId];
  }

  getClientWidth() {
    const { canvas } = this;
    return canvas ? canvas.clientWidth : 0;
  }

  getClientHeight() {
    const { canvas } = this;
    return canvas ? canvas.clientHeight : 0;
  }

  getWidth() {
    const { canvas } = this;
    return canvas ? canvas.width : 0;
  }

  getHeight() {
    const { canvas } = this;
    return canvas ? canvas.height : 0;
  }

  stopAnimation(animationId: string) {
    const { elements } = this;

    if (!this.isAvailable() && elements && animationId) {
      return;
    }

    delete elements[animationId];
  }

  playAnimation(
    animationId: string,
    animationName?: string | null,
    skinName?: string | null,
    loop?: boolean | null,
    chainAnimations?: boolean | null,
    forwards?: boolean | null,
    onFinishedCallback?: (id: string) => void,
  ) {
    const { elements } = this;

    if (!this.isAvailable()) {
      return;
    }

    const element: SpineElement = elements[animationId];

    if (element) {
      if (skinName) {
        this.setSkin(animationId, skinName);
      }
      if (animationName) {
        element.animationState.setAnimation(0, animationName, loop);
      } else {
        element.animationState.setAnimation(0, element.animations[0], loop);
      }
      element.show = true;
      element.loop = !!loop;
      element.chainAnimations = !!chainAnimations;
      element.forwards = !!forwards;
      element.onFinishedCallback = onFinishedCallback;
    }
  }

  setSkin(animationId: string, skinName: string) {
    const { elements } = this;
    const element: SpineElement = elements[animationId];

    if (element) {
      element.skeleton.setSkinByName(skinName);
    }
  }

  hasAnimationOnScreen() {
    const { elements } = this;

    return !!find(elements, (element: SpineElement) => element.show === true);
  }

  setElement(elementName: string, positionX: number, positionY: number, scale: number, scaleY?: number) {
    const { elements } = this;

    const element: SpineElement = elements[elementName];

    if (element) {
      const { skeleton } = element;

      // On positionne l'animation
      skeleton.x = positionX;
      skeleton.y = positionY;

      // On scale l'animation
      skeleton.scaleX = scale;
      skeleton.scaleY = scaleY ?? scale;
    }
  }

  hideElement(elementName: string) {
    const { elements } = this;

    const element: SpineElement = elements[elementName];

    if (element) {
      element.show = false;
    }
  }

  resize(height?: number = DEFAULT_HEIGHT, width?: number = DEFAULT_WIDTH) {
    const { renderer, gl, canvas } = this;

    if (renderer && this.isAvailable() && canvas) {
      // La camera utilise la vrai valeur en pixel
      this.renderer.camera.viewportWidth = width;
      this.renderer.camera.viewportHeight = height;

      canvas.width = width;
      canvas.height = height;

      gl.viewport(0, 0, width, height);
    }
  }

  render() {
    const { elements, timeKeeper, renderer, gl } = this;

    const shouldRender = !!find(elements, (element: SpineElement) => element.show);
    if (shouldRender) {
      this.shouldRender = true;
    }

    if (!this.isAvailable() || !this.shouldRender) {
      return;
    }

    each(elements, (element: SpineElement) => {
      if (element.show && element.animationState && element.skeleton) {
        element.animationState.update(timeKeeper.delta);
        element.animationState.apply(element.skeleton);
        element.skeleton.updateWorldTransform();
      }
    });

    if (gl) {
      gl.clear(gl.COLOR_BUFFER_BIT);
    }

    if (renderer) {
      renderer.begin();

      each(elements, (element: SpineElement) => {
        if (element.show && element.skeleton) {
          renderer.drawSkeleton(element.skeleton, true);
        }
      });

      renderer.end();

      this.shouldRender = shouldRender;
    }
  }
}

export default SpineCanvas;
